import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

function AnimatedBar({ pct, color, delay = 0 }: { pct: number; color: string; delay?: number }) {
  const [w, setW] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return;
      obs.disconnect();
      setTimeout(() => setW(pct), delay);
    }, { threshold: 0.3 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [pct, delay]);
  return <div ref={ref} className="h-full rounded-full transition-all duration-1000 ease-out" style={{ width: `${w}%`, background: color }}></div>;
}

export default function MathAlgorithmProfit() {
  const { t } = useTranslation();
  const [open, setOpen] = useState<number | null>(0);
  return (
    <section className="pb-16 md:pb-24 relative overflow-hidden" style={{ background: 'linear-gradient(180deg, #100b1e 0%, #0c0814 100%)' }}>
      <div className="absolute inset-0 grid-bg pointer-events-none opacity-40"></div>
      <div className="max-w-7xl mx-auto px-4 md:px-6 relative z-10 space-y-3">
        {/* Chapter 04 */}
        <div className={`rounded-2xl border transition-all duration-300 overflow-hidden ${open === 0 ? 'border-purple-400/20 bg-purple-400/[0.03]' : 'border-white/5 bg-white/[0.015] hover:border-purple-400/12'}`}>
          <button className="w-full flex items-center gap-3 md:gap-5 px-4 md:px-8 py-4 md:py-6 text-left cursor-pointer" onClick={() => setOpen(open === 0 ? null : 0)}>
            <div className={`w-12 md:w-14 h-12 md:h-14 rounded-2xl flex items-center justify-center shrink-0 ${open === 0 ? '' : 'bg-white/[0.04] border border-white/8'}`}
              style={open === 0 ? { background: 'linear-gradient(135deg,#9333ea,#7c3aed)' } : {}}>
              <i className={`ri-node-tree text-xl md:text-2xl ${open === 0 ? 'text-white' : 'text-purple-400'}`}></i>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-purple-400 text-xs md:text-sm font-mono font-bold">{t('mathProfit.ch3_chapter')}</span>
                <span className="hidden md:inline text-xs text-white/30 bg-white/[0.04] border border-white/5 rounded-full px-2 py-0.5">{t('mathProfit.ch3_tag')}</span>
              </div>
              <h3 className="text-sm md:text-lg font-bold text-white leading-snug">{t('mathProfit.ch3_title')}</h3>
            </div>
            <div className={`w-8 h-8 flex items-center justify-center rounded-full border transition-all shrink-0 ${open === 0 ? 'border-purple-400/40 bg-purple-400/10' : 'border-white/8'}`}>
              <i className={`ri-arrow-down-s-line text-xl transition-transform duration-300 ${open === 0 ? 'rotate-180 text-purple-400' : 'text-white/35'}`}></i>
            </div>
          </button>
          {open === 0 && (
            <div className="px-4 md:px-8 pb-6 md:pb-8">
              <div className="border-t border-purple-500/10 pt-5 md:pt-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="rounded-2xl p-6 border border-purple-500/20" style={{ background: 'rgba(168,85,247,0.05)' }}>
                      <div className="text-purple-400 text-sm font-semibold mb-4">Algorithm Convergence</div>
                      <div className="space-y-3">
                        {[
                          { t: 'Init', desc: 'Start from any feasible point', step: '0' },
                          { t: 'Linearize', desc: 'Compute current gradient', step: '1' },
                          { t: 'Add Vertex', desc: 'Select optimal vertex sₜ', step: '2' },
                          { t: 'Mix Update', desc: 'xₜ₊₁ = (1-γ)xₜ + γsₜ', step: '3' },
                          { t: 'Converge', desc: '50~150 rounds to stop', step: '✓' },
                        ].map((s, i) => (
                          <div key={i} className="flex items-center gap-4 p-3 rounded-xl border border-white/8" style={{ background: 'rgba(255,255,255,0.04)' }}>
                            <div className="w-7 h-7 rounded-full bg-purple-600 flex items-center justify-center text-white text-xs font-bold shrink-0">{s.step}</div>
                            <div>
                              <div className="text-white text-sm font-semibold">{s.t}</div>
                              <div className="text-white/40 text-xs font-mono">{s.desc}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="rounded-2xl p-6 border border-purple-500/20" style={{ background: 'rgba(168,85,247,0.05)' }}>
                      <div className="text-emerald-400 text-sm font-semibold mb-4">Key Advantages</div>
                      <div className="space-y-3">
                        {[
                          { icon: 'ri-speed-line', title: 'One vertex per round', sub: 'Avoids exponential enumeration' },
                          { icon: 'ri-shield-check-line', title: 'Faster later', sub: 'Tighter constraints' },
                          { icon: 'ri-cpu-line', title: 'Memory friendly', sub: 'No full constraint matrix' },
                        ].map(item => (
                          <div key={item.title} className="flex items-start gap-4 rounded-xl p-4 border border-purple-500/20" style={{ background: 'rgba(168,85,247,0.07)' }}>
                            <div className="w-10 h-10 rounded-lg border border-purple-500/30 flex items-center justify-center shrink-0" style={{ background: 'rgba(168,85,247,0.15)' }}>
                              <i className={`${item.icon} text-lg text-purple-400`}></i>
                            </div>
                            <div>
                              <div className="text-white text-sm font-semibold">{item.title}</div>
                              <div className="text-white/40 text-xs mt-0.5">{item.sub}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Execution Framework */}
        <div className="rounded-2xl border border-purple-500/20 overflow-hidden" style={{ background: 'rgba(168,85,247,0.04)' }}>
          <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-purple-500/15">
            <div className="p-6 space-y-3">
              <div className="text-white/40 text-xs text-center mb-2 font-medium">{t('mathProfit.execTitle')}</div>
              <div className="bg-emerald-900/30 border border-emerald-500/30 rounded-xl px-5 py-4 text-center">
                <div className="text-emerald-200 font-bold text-base mb-1">Arbitrage Plan</div>
                <div className="text-emerald-400/70 text-xs">Buy YES $0.30 + NO $0.30 → Profit $0.40</div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-xl px-4 py-3 text-center border border-emerald-500/30" style={{ background: 'rgba(16,185,129,0.1)' }}>
                  <div className="text-emerald-300 font-bold text-sm">YES Filled</div>
                  <div className="text-emerald-400/70 text-xs mt-1">$0.30 ✓</div>
                </div>
                <div className="bg-red-900/40 border border-red-500/30 rounded-xl px-4 py-3 text-center">
                  <div className="text-red-200 font-bold text-sm">NO Filled</div>
                  <div className="text-red-400/70 text-xs mt-1">$0.78 ×</div>
                </div>
              </div>
              <div className="bg-red-900/30 border border-red-500/30 rounded-xl px-5 py-4 text-center">
                <div className="text-red-200 font-bold text-base mb-1">Result: Loss $0.08</div>
                <div className="text-red-400/70 text-xs font-mono">$0.30 + $0.78 = $1.08 &gt; $1.00</div>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div className="text-white/40 text-xs text-center mb-2 font-medium">{t('mathProfit.profitStructure')}</div>
              <div className="space-y-3">
                {[
                  { label: 'Single Condition', val: '$10.6M', pct: 37, color: 'linear-gradient(90deg,#a855f7,#7c3aed)' },
                  { label: 'Market Rebalance', val: '$29.0M', pct: 100, color: 'linear-gradient(90deg,#a855f7,#7c3aed)' },
                  { label: 'Cross-market', val: '$96K', pct: 1, color: 'linear-gradient(90deg,#a855f7,#7c3aed)' },
                ].map(r => (
                  <div key={r.label}>
                    <div className="text-white/40 text-xs mb-1">{r.label}</div>
                    <div className="flex items-center gap-3">
                      <div className="flex-1 h-7 rounded-lg overflow-hidden border border-white/8" style={{ background: 'rgba(255,255,255,0.04)' }}>
                        <AnimatedBar pct={r.pct} color={r.color} />
                      </div>
                      <span className="text-white text-sm font-bold w-16 text-right shrink-0">{r.val}</span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="rounded-xl px-5 py-4 text-center border border-emerald-500/30" style={{ background: 'rgba(16,185,129,0.1)' }}>
                <div className="text-emerald-200 font-bold text-lg mb-0.5">{t('mathProfit.totalProfit')}</div>
                <div className="text-emerald-400/70 text-xs">{t('mathProfit.top10')}</div>
              </div>
              <div className="text-white/30 text-xs text-center">{t('mathProfit.kellyFormula')}</div>
              <div className="bg-purple-900/40 border border-purple-500/30 rounded-xl px-5 py-4 text-center">
                <div className="text-purple-200 font-bold text-xl font-mono mb-2">f = (b×p - q) / b × vp</div>
              </div>
              <div className="text-white/30 text-xs text-center">{t('mathProfit.preTrade')}</div>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { labelKey: 'mathProfit.gate0', highlight: false },
                  { labelKey: 'mathProfit.gate1', highlight: false },
                  { labelKey: 'mathProfit.gate2', highlight: false },
                  { labelKey: 'mathProfit.gate3', highlight: true },
                ].map(gate => (
                  <div key={gate.labelKey} className={`rounded-xl px-4 py-3 text-center text-xs font-semibold border cursor-default ${gate.highlight ? 'bg-emerald-900/40 border-emerald-500/40 text-emerald-300' : 'border-white/8 text-white/40'}`}
                    style={gate.highlight ? {} : { background: 'rgba(255,255,255,0.04)' }}>
                    {t(gate.labelKey)}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
